import { FC } from "preact/compat";
import { useMemo } from "preact/hooks";
import { useApp } from "~/core";
import HistoryTreeNode from "./HistoryTreeNode";
import { HistoryTreeProps } from "./interfaces";
import { makeHistoryTree } from "./utils";
import GraphIcon from "@material-design-icons/svg/outlined/account_tree.svg";
import ChevronLeftIcon from "@material-design-icons/svg/outlined/chevron_left.svg";
import ListIcon from "@material-design-icons/svg/outlined/list.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const MODES = [
    {
        value: "list" as const,
        label: "View as list",
        icon: ListIcon,
    },
    {
        value: "tree" as const,
        label: "View as tree",
        icon: GraphIcon,
    },
];

const HistoryTree: FC<HistoryTreeProps> = ({ tag }) => {
    const {
        api: { discovery },
        help,
    } = useApp();
    const loaded = discovery.historyLoaded(tag.tag);
    const history = discovery.getHistory(tag.tag);
    const historyTree = useMemo(
        () => makeHistoryTree(history.value),
        [history.value],
    );
    const historyFlatTree = useMemo(
        () => ({
            tag: "$root",
            children: history.value
                .map((i) => ({
                    tag: i.detailedId.toString(),
                    children: [],
                    value: i,
                }))
                .reverse(),
        }),
        [history.value],
    );
    const finalTree =
        help.admin.historyMode.value === "tree" ? historyTree : historyFlatTree;

    return (
        <div>
            <div className="flex items-center justify-between gap-x-6">
                <button
                    type="button"
                    onClick={() => {
                        help.admin.isEditorHistory.value = false;
                    }}
                    className="flex w-full items-center gap-x-2 border-b border-gray-200 fill-current p-3 hover:text-sky-500"
                >
                    <ChevronLeftIcon width="16" height="16" />
                    <span>Back</span>
                </button>
                <div className="flex items-center gap-x-3 pr-3">
                    {MODES.map(({ label, value, icon: Icon }) => (
                        <label
                            title={label}
                            key={value}
                            className={cn(
                                "flex cursor-pointer rounded-full fill-current p-1",
                                help.admin.historyMode.value === value
                                    ? "bg-sky-100 text-sky-500"
                                    : "text-gray-500 hover:bg-gray-100",
                            )}
                        >
                            <input
                                type="radio"
                                name="history-mode"
                                value={value}
                                checked={help.admin.historyMode.value === value}
                                onClick={() => {
                                    help.admin.historyMode.value = value;
                                }}
                                className="hidden"
                            />
                            <Icon width="20" height="20" />
                        </label>
                    ))}
                </div>
            </div>
            {!loaded.value && (
                <div className="p-2">
                    <Loader width="18" height="18" className="text-sky-500" />
                </div>
            )}
            <div>
                {finalTree.children.map((child) => (
                    <HistoryTreeNode
                        tag={tag}
                        key={child.tag}
                        history={history}
                        node={child}
                    />
                ))}
            </div>
            {historyTree.children.length === 0 && loaded.value && (
                <p className="p-2 text-sm text-gray-500">No revisions yet</p>
            )}
        </div>
    );
};

export default HistoryTree;
